import { Z_EMAIL, Z_FIRST_NAME, Z_JOB, Z_LAST_NAME, Z_PHONE } from '@common/schemas';
import {
  checkIfEmailIsCampagneAvailable,
  checkIfEmailIsCampagneAvailableForUpdate,
  OrganismeStatutType,
} from '@common/services';
import { sleep } from '@common/utils/promise';
import { z } from 'zod';
import { store } from '../../../../store';
import { InscriptionState } from '../../../../stores/inscription.slice';

const baseStep1FormSchema = z.object({
  firstAdminLastName: Z_LAST_NAME,
  firstAdminFirstName: Z_FIRST_NAME,
  firstAdminJob: Z_JOB,
  firstAdminPhoneNumber: Z_PHONE,
  firstAdminEmail: Z_EMAIL,
  isFirstAdminLegalRepresentative: z.boolean().optional(),
});

export const step1AsyncFormSchema = baseStep1FormSchema.superRefine(async (form, ctx) => {
  const inscriptionState = store.getState().inscription as InscriptionState;
  const isDemandeInformation = (inscriptionState.organisme?.statut === OrganismeStatutType.DEMANDE_INFORMATION);
  const siren = inscriptionState.organisme?.siren;

  const formAdmin1Email = form.firstAdminEmail?.trim();

  // Validation asynchrone de l'email 1
  if (formAdmin1Email) {
    if (isDemandeInformation) {
      const isEmailValid = await emailAsyncValidationForUpdate(formAdmin1Email, siren!);
      if (!isEmailValid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Cet email est déjà utilisé',
          path: ['firstAdminEmail'],
        });
      }
      return;
    }
    const isEmailValid = await emailAsyncValidation(formAdmin1Email);
    if (!isEmailValid) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Cet email est déjà utilisé',
        path: ['firstAdminEmail'],
      });
    }
  }
});

export const step1WithSecondAdminAsyncFormSchema = baseStep1FormSchema.safeExtend({
  secondAdminLastName: Z_LAST_NAME,
  secondAdminFirstName: Z_FIRST_NAME,
  secondAdminJob: Z_JOB,
  secondAdminPhoneNumber: Z_PHONE,
  secondAdminEmail: Z_EMAIL,
}).superRefine(async (form, ctx) => {
  const inscriptionState = store.getState().inscription as InscriptionState;
  const isDemandeInformation = (inscriptionState.organisme?.statut === OrganismeStatutType.DEMANDE_INFORMATION);
  const siren = inscriptionState.organisme?.siren;

  const formAdmin1Email = form.firstAdminEmail?.trim();
  const formAdmin2Email = form.secondAdminEmail?.trim();

  if (!!formAdmin2Email && formAdmin1Email === formAdmin2Email) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'L\'adresse mail doit être différente de celle du second administrateur.',
      path: ['firstAdminEmail'],
    });
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'L\'adresse mail doit être différente de celle du premier administrateur.',
      path: ['secondAdminEmail'],
    });
    return;
  }

  // si les emails sont bien different, on peut verifier s'ils ne sont pas déja utilisés
  // Validation asynchrone de l'email 1
  if (formAdmin1Email) {
    if (isDemandeInformation) {
      const isEmailValid = await emailAsyncValidationForUpdate(formAdmin1Email, siren!);
      if (!isEmailValid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Cet email est déjà utilisé',
          path: ['firstAdminEmail'],
        });
      }
      return;
    }

    const isEmail1Valid = await emailAsyncValidation(formAdmin1Email);
    if (!isEmail1Valid) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Cet email est déjà utilisé',
        path: ['firstAdminEmail'],
      });
    }
  }

  // Validation asynchrone de l'email 2
  if (formAdmin2Email) {
    if (isDemandeInformation) {
      const isEmailValid = await emailAsyncValidationForUpdate(formAdmin2Email, siren!);
      if (!isEmailValid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Cet email est déjà utilisé',
          path: ['secondAdminEmail'],
        });
      }
      return;
    }

    const isEmailValid = await emailAsyncValidation(formAdmin2Email);
    if (!isEmailValid) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Cet email est déjà utilisé',
        path: ['secondAdminEmail'],
      });
    }
  }
});

async function emailAsyncValidation(email: string) {
  await sleep(500);
  return checkIfEmailIsCampagneAvailable(store, { email }).then(({ data }) => data).catch(() => false);
}

async function emailAsyncValidationForUpdate(email: string, siren: string) {
  await sleep(500);
  return checkIfEmailIsCampagneAvailableForUpdate(store, { email, siren }).then(({ data }) => data).catch(() => false);
}

export type Step1WithoutSecondAdminFormResult = z.infer<typeof step1AsyncFormSchema>;
export type Step1WithSecondAdminFormResult = z.infer<typeof step1WithSecondAdminAsyncFormSchema>;
export type Step1FormResult = Step1WithoutSecondAdminFormResult | Step1WithSecondAdminFormResult;